import React, {memo, useEffect, useLayoutEffect, useRef, useState} from "react";
import {Handle, Position, Node, useReactFlow} from "@xyflow/react";
import {Icon} from "@/components/ui/icon";
import {
  Action,
  AssistantType,
  SettingParam,
  SettingParamTypes,
  WorkflowNodeData,
  WorkflowNodeTypes,
} from "@/types/node";
import {
  generateId,
  getTailwindColor,
  getSettingValueByKeys,
} from "@/utils/index";
import {getSettingValueByKey} from "@/utils/index";
import {WorkflowNodesData} from "./NodesData";
import NodeWithToolbar from "../components/NodeToolbar";
import {Zap} from "lucide-react";
import {DynamicIcon} from "../../../lib/SettingsBuilder";
import {highlightVariables} from "../../../components/settings/SettingPrompt";
import {useSelectedElements} from "@/hooks/useSelectedElements";

export function createNode(blueprint: Partial<Node>): Node<WorkflowNodeData> {
  const {
    id = generateId(),
    type,
    data = {},
    measured,
    position = {x: 0, y: 0},
  } = blueprint;

  return {
    id,
    type,
    data,
    position,
    measured,
    ...(blueprint.type === WorkflowNodeTypes.ROOT
      ? {hidden: true, connectable: false}
      : {}),
  } as Node<WorkflowNodeData>;
}

const WorkflowNode = ({
  id,
  data,
  showContext,
  assistantType,
}: {
  id: string;
  data: WorkflowNodeData;
  showContext: boolean;
  assistantType: AssistantType;
}) => {
  const {deleteElements, getNodes} = useReactFlow();
  const {getSelectedNode} = useSelectedElements();

  const isPlaceholderNode = data.type === WorkflowNodeTypes.PLACEHOLDER;
  const isGlobalNode = data.type === WorkflowNodeTypes.GLOBAL;
  const isInstructionNode = data.type === WorkflowNodeTypes.INSTRUCTION;
  const hasGroupedAttributes = isGlobalNode || isInstructionNode;
  const globalNodeAttributes = [
    "global_actions",
    "knowledge_base",
    "special_cases",
    "faq",
  ];
  const instructionNodeAttributes = ["actions", "variables"];

  const globalNodeSettings = getSettingValueByKeys(
    isGlobalNode,
    data.settings,
    globalNodeAttributes,
  );
  const instructionNodeSettings = getSettingValueByKeys(
    isInstructionNode,
    data.settings,
    instructionNodeAttributes,
  );

  const hasInstructions = data.settings?.some(setting =>
    setting.params.some(param => param.type === SettingParamTypes.PROMPT),
  );

  const actionsSettingsKey =
    data.type === WorkflowNodeTypes.START ? "global_actions" : "actions";
  const nodeActions = getSettingValueByKey(
    data.settings ?? [],
    actionsSettingsKey,
  ) as Action[];

  const actionsContainerRef = useRef(null);
  const prevHiddenActionCount = useRef(0);
  const [hiddenActionCount, setHiddenActionCount] = useState(0);

  const selectedNode = getSelectedNode();
  const isNodeSelected = selectedNode?.id === id;

  const [isToolbarVisible, setIsToolbarVisible] = useState(isNodeSelected);

  const nodeConfig =
    WorkflowNodesData[data.type as keyof typeof WorkflowNodesData];

  const contextColor = getTailwindColor(nodeConfig.styles.contextColor);
  const nodeStyles = {
    icon: {
      color: nodeConfig.styles.iconColor,
      backgroundColor: nodeConfig.styles.iconBackgroundColor,
      borderColor: nodeConfig.styles.iconBorderColor,
    },
  };

  const nodeName =
    getSettingValueByKey(data.settings ?? [], "name") ||
    WorkflowNodesData[data.type].blueprint?.data.settingsTitle ||
    "New Stage";
  const nodeTrigger = getSettingValueByKey(data.settings ?? [], "trigger");
  const nodeInstructions = getSettingValueByKey(
    data.settings ?? [],
    "instructions",
  );
  const nodeScriptedGreeting = getSettingValueByKey(
    data.settings ?? [],
    "scripted_greeting",
  );
  const nodeGlobalInstructions = getSettingValueByKey(
    data.settings ?? [],
    "global_instructions",
  );

  const nodes = getNodes();
  const instructionsText = highlightVariables(
    (nodeInstructions as string) ||
      (nodeGlobalInstructions as string) ||
      (nodeScriptedGreeting as string),
    nodes,
    assistantType,
  );

  useLayoutEffect(() => {
    if (!actionsContainerRef.current) return;

    const checkOverflow = () => {
      const container = actionsContainerRef.current;
      const actionItems = [...container.children].filter(
        child => !child.classList.contains("hidden-count"),
      );

      let totalWidth = 0;
      const visibleActions = actionItems.filter(child => {
        totalWidth += (child as HTMLElement).offsetWidth;
        return totalWidth <= 278;
      });

      const newHiddenActionCount = nodeActions.length - visibleActions.length;

      if (prevHiddenActionCount.current !== newHiddenActionCount) {
        prevHiddenActionCount.current = newHiddenActionCount;
        setHiddenActionCount(Math.max(0, newHiddenActionCount));
      }
    };

    checkOverflow();
  }, [nodeActions]);

  useEffect(() => {
    setIsToolbarVisible(isNodeSelected);
  }, [isNodeSelected]);

  if (!nodeConfig) {
    console.error(`No configuration found for node type: ${data.type}`);
    return null;
  }

  return (
    <div
      onMouseEnter={() => setIsToolbarVisible(true)}
      onMouseLeave={() => !isNodeSelected && setIsToolbarVisible(false)}
    >
      <div
        className={`node w-85 bg-white border rounded-lg shadow-node transition-all ${isPlaceholderNode ? "invisible" : ""} ${showContext ? "enableContext" : ""}`}
        style={
          showContext
            ? ({
                "--overlay-color": contextColor,
              } as React.CSSProperties)
            : {}
        }
      >
        {!isPlaceholderNode && (
          <>
            <div className="flex items-center gap-3 px-3 py-3.5">
              <Icon className={"border"} style={nodeStyles.icon}>
                {nodeConfig.icon &&
                  React.createElement(nodeConfig.icon, {
                    color: nodeStyles.icon.color,
                  })}
              </Icon>
              <div className="leading-none min-w-0">
                <h3 className="text-base font-medium hasContext overflow-hidden whitespace-nowrap text-ellipsis">
                  {nodeName.toString()}
                </h3>
                {nodeTrigger !== null && (
                  <span className="text-sm text-secondary">
                    {nodeTrigger?.toString() || "No Trigger"}
                  </span>
                )}
              </div>
            </div>
            {hasInstructions && (
              <div
                className={`text-sm p-3 border-t overflow-hidden ${nodeInstructions || nodeGlobalInstructions || nodeScriptedGreeting ? "text-primary" : "text-disabled"}`}
              >
                <span
                  className="line-clamp-2 overflow-hidden text-ellipsis break-words hasContext"
                  dangerouslySetInnerHTML={{
                    __html: instructionsText || "No instructions added",
                  }}
                ></span>
              </div>
            )}
            {hasGroupedAttributes && (
              <div className="text-sm flex gap-2 px-3 overflow-hidden">
                {(() => {
                  const nodeSettings = isGlobalNode
                    ? globalNodeSettings
                    : instructionNodeSettings;

                  return nodeSettings.map(setting => {
                    const numberOfParams = Array.isArray(
                      setting.params[0].value,
                    )
                      ? setting.params[0].value.length
                      : 0;
                    if (numberOfParams <= 0) return null;

                    return (
                      <div
                        key={setting.title}
                        className="border rounded-md p-1.5 mb-3 flex items-center gap-2 hasContext text-label"
                      >
                        <div className="border-1 bg-primary-accent w-4 h-4">
                          {setting.icon && (
                            <DynamicIcon
                              iconName={setting.icon}
                              className="h-4 w-4 mr-1.5"
                            />
                          )}
                        </div>
                        <span className="whitespace-nowrap overflow-ellipsis overflow-hidden leading-4">
                          {numberOfParams}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            {nodeActions?.length > 0 && !hasGroupedAttributes && (
              <div
                ref={actionsContainerRef}
                className="text-sm p-3 pt-0 flex gap-2 overflow-hidden"
              >
                {nodeActions
                  .slice(0, nodeActions.length - hiddenActionCount)
                  .map(action => (
                    <div
                      key={action.action_id}
                      className="border rounded p-1 flex items-center gap-2 hasContext"
                    >
                      <div className="border-1 bg-primary-accent w-4 h-4">
                        <Zap className="w-4 h-4" />
                      </div>
                      <span className="whitespace-nowrap overflow-ellipsis overflow-hidden">
                        {action.name}
                      </span>
                    </div>
                  ))}
                {hiddenActionCount > 0 && (
                  <span className="border rounded p-1 px-1.5 hidden-count hasContext">
                    +{hiddenActionCount}
                  </span>
                )}
              </div>
            )}
            <NodeWithToolbar
              nodeId={id}
              nodeType={data.type}
              isToolbarVisible={isToolbarVisible}
              onDelete={() => deleteElements({nodes: [{id}]})}
              onMouseEnter={() => setIsToolbarVisible(true)}
              onMouseLeave={() => isNodeSelected && setIsToolbarVisible(false)}
            />
          </>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className={"handle"}
        id={`${id}-top`}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className={"handle"}
        id={`${id}-bottom`}
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={true}
        className={"handle"}
        id={`${id}-right-source`}
      />
      <Handle
        type="source"
        position={Position.Left}
        isConnectable={true}
        className={"handle"}
        id={`${id}-left-source`}
      />
    </div>
  );
};

export default memo(WorkflowNode);
